import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import {
  LayoutDashboard,
  Building2,
  FileText,
  Receipt,
  BookOpen,
  BarChart3,
  Download,
  FolderOpen,
  LogOut,
  ChevronDown,
  Landmark,
  Workflow,
  ArrowLeftRight,
  Users,
  CreditCard,
  Bell,
  Calculator,
  Settings,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { apiFetch, API } from "@/utils/api";
import { getCompanyProfile } from "@/utils/companyProfile";
import AiChat from "@/components/AiChat";
import MobileNav from "@/mobile/MobileNav";

// Contenido completo de la barra de navegación (logo + nav + usuario).
// Se usa dos veces: en el aside fijo de escritorio y dentro del drawer móvil.
// onNavigate se dispara al pulsar cualquier destino (en móvil cierra el drawer).
function SidebarContent({ user, onNavigate, onLogout, onSettings }) {
  return (
    <div className="flex flex-col h-full w-full bg-white">
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <h1 className="font-heading text-xl font-bold text-slate-900" data-testid="app-logo">
          LiquidArc
        </h1>
        <p className="text-xs text-slate-500 mt-1">Contabilidad PGC</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {/* Dashboard */}
        <NavLink
          to="/dashboard"
          onClick={onNavigate}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          data-testid="nav-dashboard"
        >
          <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />
          Dashboard
        </NavLink>

        {/* Banco */}
        <DropdownMenu>
          <DropdownMenuTrigger className="sidebar-link w-full justify-between" data-testid="nav-banco">
            <span className="flex items-center gap-3">
              <Building2 className="w-5 h-5" strokeWidth={1.5} />
              Banco
            </span>
            <ChevronDown className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem asChild>
              <NavLink to="/banco/movimientos" onClick={onNavigate} className="flex items-center gap-2 w-full" data-testid="nav-movimientos">
                <Landmark className="w-4 h-4" strokeWidth={1.5} />
                Movimientos
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <NavLink to="/banco/reglas" onClick={onNavigate} className="flex items-center gap-2 w-full" data-testid="nav-reglas">
                <Workflow className="w-4 h-4" strokeWidth={1.5} />
                Reglas
              </NavLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Conciliacion */}
        <NavLink
          to="/conciliacion"
          onClick={onNavigate}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          data-testid="nav-conciliacion"
        >
          <ArrowLeftRight className="w-5 h-5" strokeWidth={1.5} />
          Conciliacion
        </NavLink>

        {/* Facturas */}
        <DropdownMenu>
          <DropdownMenuTrigger className="sidebar-link w-full justify-between" data-testid="nav-facturas">
            <span className="flex items-center gap-3">
              <Receipt className="w-5 h-5" strokeWidth={1.5} />
              Facturas
            </span>
            <ChevronDown className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem asChild>
              <NavLink to="/facturas/recibidas" onClick={onNavigate} className="flex items-center gap-2 w-full" data-testid="nav-recibidas">
                <FileText className="w-4 h-4" strokeWidth={1.5} />
                Recibidas
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <NavLink to="/facturas/emitidas" onClick={onNavigate} className="flex items-center gap-2 w-full" data-testid="nav-emitidas">
                <FileText className="w-4 h-4" strokeWidth={1.5} />
                Emitidas
              </NavLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* CRM Section */}
        <div className="pt-3 mt-3 border-t border-slate-200">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold px-3 mb-2">CRM</p>
        </div>

        <NavLink
          to="/clientes"
          onClick={onNavigate}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          data-testid="nav-clientes"
        >
          <Users className="w-5 h-5" strokeWidth={1.5} />
          Clientes
        </NavLink>

        <NavLink
          to="/suscripciones"
          onClick={onNavigate}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          data-testid="nav-suscripciones"
        >
          <CreditCard className="w-5 h-5" strokeWidth={1.5} />
          Suscripciones
        </NavLink>

        <NavLink
          to="/alertas"
          onClick={onNavigate}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          data-testid="nav-alertas"
        >
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          Alertas
        </NavLink>

        <NavLink
          to="/presupuestos"
          onClick={onNavigate}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          data-testid="nav-presupuestos"
        >
          <Calculator className="w-5 h-5" strokeWidth={1.5} />
          Presupuestos
        </NavLink>

        {/* Contabilidad Section */}
        <div className="pt-3 mt-3 border-t border-slate-200">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold px-3 mb-2">Contabilidad</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger className="sidebar-link w-full justify-between" data-testid="nav-contabilidad">
            <span className="flex items-center gap-3">
              <BookOpen className="w-5 h-5" strokeWidth={1.5} />
              Contabilidad
            </span>
            <ChevronDown className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem asChild>
              <NavLink to="/contabilidad/plan" onClick={onNavigate} className="flex items-center gap-2 w-full" data-testid="nav-plan">
                <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                Plan de Cuentas
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <NavLink to="/contabilidad/diario" onClick={onNavigate} className="flex items-center gap-2 w-full" data-testid="nav-diario">
                <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                Libro Diario
              </NavLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Informes */}
        <NavLink
          to="/informes"
          onClick={onNavigate}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          data-testid="nav-informes"
        >
          <BarChart3 className="w-5 h-5" strokeWidth={1.5} />
          Informes
        </NavLink>

        {/* Documentos */}
        <NavLink
          to="/documentos"
          onClick={onNavigate}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          data-testid="nav-documentos"
        >
          <FolderOpen className="w-5 h-5" strokeWidth={1.5} />
          Documentos
        </NavLink>

        {/* Exportar */}
        <NavLink
          to="/exportar"
          onClick={onNavigate}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          data-testid="nav-exportar"
        >
          <Download className="w-5 h-5" strokeWidth={1.5} />
          Exportar
        </NavLink>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-slate-200">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="user-menu">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-slate-900 text-white text-xs">
                {user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.name || "Usuario"}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {user?.email || ""}
              </p>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={onSettings} data-testid="settings-btn">
              <Settings className="w-4 h-4 mr-2" />
              Ajustes
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onLogout} className="text-red-600" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function Layout({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Onboarding: usuarios nuevos (con perfil) sin identidad básica → wizard.
  // Los usuarios legacy (has_profile false) nunca lo ven.
  useEffect(() => {
    if (location.pathname === "/onboarding" || location.pathname === "/ajustes") return;
    let cancelled = false;
    const checkOnboarding = async () => {
      try {
        const p = await getCompanyProfile();
        if (cancelled) return;
        const id = p?.identidad ?? {};
        if (p?.has_profile === true && (!id.name || !id.nif)) {
          navigate("/onboarding");
        }
      } catch {
        // Si el perfil no carga, no bloqueamos la navegación
      }
    };
    checkOnboarding();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  const handleLogout = async () => {
    try {
      await apiFetch(`${API}/auth/logout`, {
        method: "POST",
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    navigate("/login");
  };

  const handleSettings = () => {
    navigate("/ajustes");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar (solo escritorio) */}
      <aside className="sidebar fixed left-0 top-0 h-screen hidden lg:flex flex-col z-50">
        <SidebarContent
          user={user}
          onLogout={handleLogout}
          onSettings={handleSettings}
        />
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 ml-0 lg:ml-[260px] p-4 pb-24 lg:p-8 lg:pb-8">
        <div className="max-w-[1500px] mx-auto">{children}</div>
      </main>
      <MobileNav />

      {/* AI Chat widget - floating, visible on all pages */}
      <AiChat />
    </div>
  );
}
